import React from "react";
import { ArrowLeftStartOnRectangleIcon, CheckIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast, ToastContainer } from "react-toastify";
import type { ToastContainerProps } from "react-toastify";

import BadgeIcon, { EBadgeColor } from "../BadgeIcon";
import IconButton from "../IconButton";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import Loader from "../Loader";
import classes from "./classes.module.scss";
import ToastContent from "./ToastContent";

import "react-toastify/dist/ReactToastify.css";

const CloseButton: NonNullable<ToastContainerProps["closeButton"]> = ({ closeToast }) => {
	return (
		<IconButton
			icon={<XMarkIcon />}
			onClick={(event) => closeToast?.(event)}
			ariaLabel={COMMON_UI_I18N.iconButtonAccessibilityLabels.closeNotification}
		/>
	);
};

export default function Toaster(): React.JSX.Element {
	return (
		<ToastContainer
			className={classes["root"]}
			toastClassName={classes["wrapper"]}
			progressClassName={classes["progress"]}
			closeButton={CloseButton}
		/>
	);
}

export class ToasterService {
	private static instance: ToasterService;
	private constructor() {
		void 0;
	}

	public static getInstance(): ToasterService {
		return (this.instance ??= new this());
	}

	public text({ title, description }: { title: string; description?: string }): void {
		toast.info(<ToastContent title={title} description={description} />, {
			icon: false,
		});
	}

	public info({ title, description }: { title: string; description?: string }): void {
		toast.info(
			<ToastContent
				title={title}
				description={description}
				icon={<BadgeIcon icon={<InformationCircleIcon />} color={EBadgeColor.INFO} />}
			/>,
			{
				icon: false,
			},
		);
	}

	public success({ title, description }: { title: string; description?: string }): void {
		toast.info(
			<ToastContent title={title} description={description} icon={<BadgeIcon icon={<CheckIcon />} color={EBadgeColor.SUCCESS} />} />,
			{
				icon: false,
			},
		);
	}

	public error({ title, description, toastId }: { title: string; description?: string; toastId?: string }): void {
		toast.info(
			<ToastContent title={title} description={description} icon={<BadgeIcon icon={<XMarkIcon />} color={EBadgeColor.ERROR} />} />,
			{
				icon: false,
				toastId,
			},
		);
	}

	/** Dismisses an active toast by id (used to re-arm the single network-error toast on success). */
	public dismiss(toastId: string): void {
		toast.dismiss(toastId);
	}

	public warning({ title, description }: { title: string; description?: string }): void {
		toast.info(
			<ToastContent
				title={title}
				description={description}
				icon={<BadgeIcon icon={<ExclamationTriangleIcon />} color={EBadgeColor.WARNING} />}
			/>,
			{
				icon: false,
			},
		);
	}

	public warn(payload: { title: string; description?: string }): void {
		this.warning(payload);
	}

	public loading({ title, description }: { title: string; description?: string }): void {
		toast.info(
			<ToastContent title={title} description={description} icon={<BadgeIcon icon={<Loader />} color={EBadgeColor.INFO} />} />,
			{
				icon: false,
				autoClose: false,
			},
		);
	}

	public loggedOut({ title, description }: { title: string; description?: string }): void {
		toast.info(
			<ToastContent
				title={title}
				description={description}
				icon={<BadgeIcon icon={<ArrowLeftStartOnRectangleIcon />} color={EBadgeColor.NEUTRAL} />}
			/>,
			{
				icon: false,
				autoClose: false,
			},
		);
	}
}
